import React, { useCallback, useState } from 'react';
import gql from 'graphql-tag';
import styled from 'styled-components';
import { Button, CardHeader, CardSection, Flex, Heading, ModalDialog } from '@zillow/constellation';

import ContactAgentFormContent from './ContactAgentFormContent';
import AbadContent from './AbadContent';
import SuccessContent from './SuccessContent';

import { DisplayState } from '../constants';
import { getCopy, SEBASTIAN_CMA_FORM_VERSION } from '../utils/copyUtils';
import type {
    ContactAgentProps,
    DisplayProps,
    ModalDisplayProps,
    SubmitContactParams,
} from '../types/@zillow-types';

const ModalDisplay = ({ modalDialogProps, header, body, footer }: ModalDisplayProps) => (
    <ModalDialog
        data-testid="contact-agent-form-modal"
        wide
        {...modalDialogProps}
        header={header && <Heading level={4}>{header}</Heading>}
        body={body}
        footer={footer}
    />
);

const StyledCardHeader = styled(CardHeader)<{ centerAlignedNote?: boolean }>`
    text-align: ${(props) => (props.centerAlignedNote ? 'center' : 'left')};
`;

const CardDisplay = ({ header, body, footer, centerAlignedNote }: DisplayProps) => (
    <React.Fragment>
        {header && (
            <StyledCardHeader centerAlignedNote={centerAlignedNote}>{header}</StyledCardHeader>
        )}
        <CardSection>{body}</CardSection>
        {footer && <CardSection>{footer}</CardSection>}
    </React.Fragment>
);

const INITIAL_DISPLAY_STATE = DisplayState.FORM;

const ContactAgentContainer = ({
    property,
    viewer,
    page,
    location,
    treatment = 'pl-lead-form',
    onSubmit,
    onFetchAbadHtml,
    showCheckBox,
    centerAlignedNote,
    modalDialogProps,
    shouldSuggestCloseOnSuccess,
    onClose,
    abadClient,
    zatlasEndpoint,
    contactFormVersion,
    trackingParam,
}: ContactAgentProps) => {
    const [displayState, setDisplayState] = useState<DisplayState>(INITIAL_DISPLAY_STATE);
    const [abadHtml, setAbadHtml] = useState<string>('');
    const isModal = !!modalDialogProps;

    const formVersion = trackingParam?.toLowerCase().includes('_sebcmatest')
        ? SEBASTIAN_CMA_FORM_VERSION
        : contactFormVersion;

    const handleClose = useCallback(() => {
        // Reset for next time modal is opened
        setDisplayState(INITIAL_DISPLAY_STATE);
        if (onClose) {
            onClose();
        }
    }, [setDisplayState, onClose]);

    // Retain ModalDisplay constructor between renders
    const StableModalDisplay = useCallback(
        (props: DisplayProps) => {
            if (!modalDialogProps) {
                return null;
            }
            const modalDisplayProps: ModalDisplayProps = {
                ...props,
                modalDialogProps: {
                    ...modalDialogProps,
                    onClose: handleClose,
                },
            };
            return <ModalDisplay {...modalDisplayProps} />;
        },
        [modalDialogProps, handleClose]
    );
    const DisplayComponent = isModal ? StableModalDisplay : CardDisplay;

    // Default to 'true' for modals
    const shouldShowSuccessCloseButton =
        shouldSuggestCloseOnSuccess === true ||
        (typeof shouldSuggestCloseOnSuccess === 'undefined' && isModal);

    const handleSubmit = async (params: SubmitContactParams) => onSubmit(params);

    const handleSubmitSuccess = async () => {
        try {
            const response = await onFetchAbadHtml({ viewer, abadClient });
            if (response.text) {
                setAbadHtml(response.text);
                setDisplayState(DisplayState.ABAD_CONTENT);
            } else {
                // User has seen it or unexpectedly, no text was returned on response
                setDisplayState(DisplayState.SUCCESS);
            }
        } catch (e) {
            setDisplayState(DisplayState.SUCCESS);
        }
    };

    switch (displayState) {
        case DisplayState.FORM:
            return (
                <DisplayComponent
                    header={getCopy(formVersion, 'formHeader')}
                    body={
                        <ContactAgentFormContent
                            centerAlignedNote={centerAlignedNote}
                            location={location}
                            onSubmit={handleSubmit}
                            onSubmitSuccess={handleSubmitSuccess}
                            page={page}
                            treatment={treatment}
                            property={property}
                            showCheckBox={showCheckBox}
                            viewer={viewer}
                            zatlasEndpoint={zatlasEndpoint}
                            contactFormVersion={formVersion}
                            trackingParam={trackingParam}
                        />
                    }
                    footer={null}
                    centerAlignedNote={centerAlignedNote}
                />
            );
        case DisplayState.ABAD_CONTENT:
            return (
                <DisplayComponent
                    header={
                        <React.Fragment>
                            What you need to know about <strong>Zillow</strong> and its affiliated
                            businesses
                        </React.Fragment>
                    }
                    body={<AbadContent isModal={isModal} abadHtml={abadHtml} />}
                    footer={
                        <Flex display="flex" justifyContent="flex-end">
                            <Button
                                buttonType="primary"
                                data-testid="confirm-abad-button"
                                onClick={() => setDisplayState(DisplayState.SUCCESS)}
                                type="button"
                            >
                                Ok, got it!
                            </Button>
                        </Flex>
                    }
                    centerAlignedNote={centerAlignedNote}
                />
            );
        case DisplayState.SUCCESS:
            return (
                <DisplayComponent
                    header="Message sent"
                    body={
                        <SuccessContent
                            shouldShowPrimaryCloseButton={shouldShowSuccessCloseButton}
                            onClose={handleClose}
                            contactFormVersion={formVersion}
                        />
                    }
                    footer={null}
                    centerAlignedNote={centerAlignedNote}
                />
            );
        default:
            throw new Error(`Display state not supported: ${displayState as DisplayState}`);
    }
};

ContactAgentContainer.fragments = {
    property: gql`
        fragment ContactAgentForm_property on Property {
            streetAddress
            state
            city
            zipcode
            zpid
            homeStatus
            homeType
            zestimate
            homeType
            isInstantOfferEnabled
            zillowOfferMarket {
                name
                code
            }
        }
    `,
    viewer: gql`
        fragment ContactAgentForm_viewer on Viewer {
            name
            email
            zuid
        }
    `,
    abTests: gql`
        fragment ContactAgentForm_abTests on ABTests {
            SHOW_PL_LEAD_FORM: abTest(trial: "SHOW_PL_LEAD_FORM")
        }
    `,
};

export default ContactAgentContainer;
